import { createContext, useContext } from 'react';
import type { SerializedDirectedVideo } from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import type { DirectedScreen, DirectedWizardStep } from './directedSteps';

/** The song facts that the wizard shows (the top bar, "What the director reads"). */
export interface DirectedSongInfo {
  title: string;
  artistName: string;
  /** A full link (`getFullAPIFileURL`), or null with no cover. */
  artworkUrl: string | null;
  /** Null with no ready master. */
  durationSec: number | null;
  /** Null before the song map. */
  bpm: number | null;
  sections: MusicVideoSongMapSection[];
  lyricsStatus: 'none' | 'syncing' | 'synced' | 'failed';
  lyricLineCount: number;
}

/**
 * What every step of the directed video wizard reads (design directed-video b to g): the video, its
 * song, the screen, and the moves of the frame. The frame (`DirectedVideoScreen`) provides it; each
 * step reads it with `useDirectedVideoContext`.
 */
export interface DirectedVideoContextValue {
  accountId: number;
  catalogItemUuid: string;
  video: SerializedDirectedVideo;
  song: DirectedSongInfo;
  /** The screen on view (a step of `?step=`, or the step of the data). */
  screen: DirectedScreen;
  /** The step of the data: where the video is now. */
  currentScreen: DirectedScreen;
  /** The example artist: every write is off. */
  readOnly: boolean;
  /** Opens an earlier step again (`?step=`, pushed: Back returns). Null opens the current step. */
  openStep: (step: DirectedWizardStep | null) => void;
  /**
   * True on "How it was made" (design l1, `?view=made`): only after the make started. `madeStep` is
   * the step that the page shows first, or null for the top.
   */
  isMadeView: boolean;
  madeStep: DirectedWizardStep | null;
  /** Opens "How it was made" at a step (pushed: Back returns to the video). */
  openMade: (step: DirectedWizardStep | null) => void;
  /** Opens the music video editor on the cut of the video (`?cut=`). */
  openEditor: (cutUuid: string) => void;
  /** "Song": back to the page that opened the wizard, else the song page. */
  leave: () => void;
}

export const DirectedVideoContext = createContext<DirectedVideoContextValue | null>(null);

export function useDirectedVideoContext(): DirectedVideoContextValue {
  const value = useContext(DirectedVideoContext);
  if (value === null) {
    throw new Error('useDirectedVideoContext needs a DirectedVideoContext provider');
  }
  return value;
}
